import { ReactElement, ReactNode, useEffect, useRef, useState, useCallback } from 'react';
import clsx from 'clsx';
import { useAuth } from '@common/auth/use-auth';
import { NotificationDialogTrigger } from '@common/notifications/dialog/notification-dialog-trigger';
import { Menu, MenuTrigger } from '@common/ui/navigation/menu/menu-trigger';
import { useCustomMenu } from '@common/menus/use-custom-menu';
import { createSvgIconFromTree } from '@common/icons/create-svg-icon';
import { Trans } from '@common/i18n/trans';
import { IconButton } from '@common/ui/buttons/icon-button';
import { Item } from '@common/ui/forms/listbox/item';
import { useNavigate } from '@common/utils/hooks/use-navigate';
import { useIsDarkMode } from '@common/ui/themes/use-is-dark-mode';
import { CustomMenu } from '@common/menus/custom-menu';
import { useSettings } from '@common/core/settings/use-settings';
import { ButtonColor } from '@common/ui/buttons/get-shared-button-style';
import { MenuIcon } from '@common/icons/material/Menu';
import { MenuItemConfig } from '@common/core/settings/settings';
import { Button } from '../../buttons/button';
import {
  NavbarAuthUser,
  NavbarAuthUserProps,
} from '@common/ui/navigation/navbar/navbar-auth-user';
import { NavbarAuthButtons } from '@common/ui/navigation/navbar/navbar-auth-buttons';
import { useDarkThemeVariables } from '@common/ui/themes/use-dark-theme-variables';
import { Logo } from '@common/ui/navigation/navbar/logo';
import { useLightThemeVariables } from '@common/ui/themes/use-light-theme-variables';
import { isAbsoluteUrl } from '@common/utils/urls/is-absolute-url';
import { useLocation } from 'react-router-dom';
import { useMediaQuery } from '@common/utils/hooks/use-media-query';

type NavbarColor = 'primary' | 'bg' | 'bg-alt' | 'transparent' | string;

export interface NavbarProps {
  hideLogo?: boolean | null;
  toggleButton?: ReactElement;
  children?: ReactNode;
  className?: string;
  color?: NavbarColor;
  bgOpacity?: number | string;
  darkModeColor?: NavbarColor;
  logoColor?: 'dark' | 'light';
  textColor?: string;
  primaryButtonColor?: ButtonColor;
  border?: string;
  size?: 'xs' | 'sm' | 'md';
  rightChildren?: ReactNode;
  menuPosition?: string;
  authMenuItems?: NavbarAuthUserProps['items'];
  alwaysDarkMode?: boolean;
  wrapInContainer?: boolean;
}
export function Navbar(props: NavbarProps) {
  let {
    hideLogo,
    toggleButton,
    children,
    className,
    border,
    size = 'md',
    color,
    textColor,
    darkModeColor,
    rightChildren,
    menuPosition,
    logoColor,
    primaryButtonColor,
    authMenuItems,
    alwaysDarkMode = false,
    wrapInContainer = false,
  } = props;
  const location = useLocation();
  const isDarkMode = useIsDarkMode() || alwaysDarkMode;
  const { notifications } = useSettings();
  const { isLoggedIn } = useAuth();
  const darkThemeVars = useDarkThemeVariables();
  const lightThemeVars = useLightThemeVariables();
  const showNotifButton = isLoggedIn && notifications?.integrated;
  color = color ?? lightThemeVars?.['--be-navbar-color'] ?? 'primary';
  darkModeColor =
    darkModeColor ?? darkThemeVars?.['--be-navbar-color'] ?? 'bg-alt';


  const [showSearch, setShowSearch] = useState(false)
  const [showQuickLink, setShowQuickLink] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const [showMessage, setShowMessage] = useState(false)
  const [showChatSetting, setShowChatSetting] = useState(false)
  const [showFax, setShowFax] = useState(false)
  const [showBroadcast, setShowBroadcast] = useState(false)
  const [showCall, setShowCall] = useState(false)

  const [showActive, setShowActive] = useState(false)


  const quickLinkRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const messageRef = useRef<HTMLDivElement | null>(null);
  const chatRef = useRef<HTMLDivElement | null>(null);
  const faxRef = useRef<HTMLDivElement | null>(null);
  const broadcastRef = useRef<HTMLDivElement | null>(null);
  const callRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (quickLinkRef.current && !quickLinkRef.current.contains(target)) {
        setShowQuickLink(false);
      }
      if (menuRef.current && !menuRef.current.contains(target)) {
        setShowMenu(false);
      }
      if (messageRef.current && !messageRef.current.contains(target)) {
        setShowMessage(false);
      }
      if (faxRef.current && !faxRef.current.contains(target)) {
        setShowFax(false);;
      }
      if (broadcastRef.current && !broadcastRef.current.contains(target)) {
        setShowBroadcast(false);
      }
      if (callRef.current && !callRef.current.contains(target)) {
        setShowCall(false);
      }
      if (chatRef.current && !chatRef.current.contains(target)) {
        setShowChatSetting(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (isDarkMode) {
    color = darkModeColor;
  }

  return (
    <div
      style={alwaysDarkMode ? darkThemeVars : undefined}
      className={clsx(
        getColorStyle(color, textColor),
        size === 'md' && 'h-64 py-8',
        size === 'sm' && 'h-54 py-4',
        size === 'xs' && 'h-48 py-4',
        border,
        className,
      )}
    >
      <div
        className={clsx(
          'flex h-full items-center justify-end gap-10 px-10',
          wrapInContainer && 'container mx-auto',
        )}
      >
        {/* {!hideLogo && (
          <Logo isDarkMode={isDarkMode} color={color} logoColor={logoColor} />
        )} */}
        <div className="flex items-center gap-4">
          {rightChildren}

          {isLoggedIn ? (
            <NavbarAuthUser items={authMenuItems} />
          ) : (
            <NavbarAuthButtons
              navbarColor={color}
              primaryButtonColor={primaryButtonColor}
            />
          )}

          <div className="header-search-area">
            <span id="headerSearchBtn" className="btn mobile-search-btn md:none" onClick={() => setShowSearch(!showSearch)}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-search"
                {...props}
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
            </span>
            <div className={`search-box-icon align-items-center ${showSearch ? 'active' : ""}`}>
              <div className="w-full flex align-items-center">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.3-4.3" />
                  </svg>
                <input type="text" placeholder="Search" />
              </div>
            </div>
          </div>
        </div>
        <div className="header-buttons flex justify-center lg:ms-auto ms-auto">
          <div className="flex top-navbaar-center-icons justify-between align-center">
            <a
              href="https://aicpanel.smsit.ai/home"
              data-bs-toggle="tooltip"
              data-bs-title="Home"
              data-bs-placement="bottom"
            >
              <svg
                version="1.1"
                x="0px"
                y="0px"
                viewBox="0 0 50 50"
                xmlSpace="preserve"
              >
                <path d="M49.6,24.7L42.7,19V6.4c0-0.6-0.5-1.1-1.1-1.1h-5.1c-0.6,0-1.1,0.5-1.1,1.1v6.6l-7.8-6.5c-1.6-1.3-3.8-1.3-5.4,0L0.4,24.5c-0.5,0.4-0.5,1.1-0.1,1.5l2.3,2.7c0.4,0.4,1,0.5,1.5,0.1l19.6-16.4c0.8-0.7,2.1-0.7,2.9,0l19.7,16.3c0.5,0.4,1.2,0.3,1.5-0.2l2-2.6C50.1,25.7,50.1,25,49.6,24.7z" />
                <path d="M25.7,15.4c-0.4-0.3-1-0.3-1.4,0L9,28.1c-0.4,0.3-0.6,0.7-0.6,1.2v13.7c0,0.9,0.7,1.6,1.6,1.6h9.1c0.9,0,1.6-0.7,1.6-1.6v-8.2c0-0.9,0.7-1.6,1.6-1.6h5.6c0.9,0,1.6,0.7,1.6,1.6v8.2c0,0.9,0.7,1.6,1.6,1.6H40c0.9,0,1.6-0.7,1.6-1.6V29.9c0-0.5-0.2-0.9-0.5-1.2L25.7,15.4z" />
              </svg>
            </a>
            <a
              href="https://aicpanel.smsit.ai/smart-tool"
              data-bs-toggle="tooltip"
              data-bs-title="Smart Tools"
              data-bs-placement="bottom"
              aria-hidden="true"
            >
              <svg
                version="1.1"
                x="0px"
                y="0px"
                viewBox="0 0 50 50"
                xmlSpace="preserve"
              >
                <path d="M46.2,5H3.8C1.7,5,0,6.7,0,8.8v24.5c0,2.1,1.7,3.8,3.8,3.8h18.8v2.6H8.3c-0.5,0-0.9,0.4-0.9,0.9v3.4c0,0.5,0.4,0.9,0.9,0.9h33.3c0.5,0,0.9-0.4,0.9-0.9v-3.4c0-0.5-0.4-0.9-0.9-0.9H27.4v-2.6h18.8c2.1,0,3.8-1.7,3.8-3.8V8.8C50,6.7,48.3,5,46.2,5L46.2,5zM4.7,32.4V9.7h40.5v22.6H4.7L4.7,32.4z" />
              </svg>
            </a>
            <a
              href="https://aicpanel.smsit.ai/redirect-workflow/ai-insights"
              data-bs-toggle="tooltip"
              data-bs-title="Workflow Automation"
              data-bs-placement="bottom"
            >
              <svg
                version="1.1"
                x="0px"
                y="0px"
                viewBox="0 0 50 50"
                xmlSpace="preserve"
              >
                <path d="M0,26.4v7.5c0,2.2,1.8,4,4,4h0.8V22.4H4C1.8,22.4,0,24.2,0,26.4z" />
                <path d="M46,22.4h-0.8v15.5H46c2.2,0,4-1.8,4-4v-7.5C50,24.2,48.2,22.4,46,22.4z" />
                <path d="M36.9,12.4h-9.2V7.1c0-1.4-1.1-2.6-2.6-2.6s-2.6,1.1-2.6,2.6v5.3h-9.4c-3.2,0-5.9,2.6-5.9,5.9v21.2c0,3.2,2.6,5.9,5.9,5.9h23.8c3.2,0,5.9-2.6,5.9-5.9V18.3C42.8,15,40.2,12.4,36.9,12.4z M18.7,37.8h-2.3c-0.8,0-1.5-0.7-1.5-1.5c0-0.8,0.7-1.5,1.5-1.5h2.3c0.8,0,1.5,0.7,1.5,1.5C20.2,37.2,19.5,37.8,18.7,37.8z M17.5,28.3c-1.8,0-3.2-1.4-3.2-3.2c0-1.8,1.4-3.2,3.2-3.2c1.8,0,3.2,1.4,3.2,3.2C20.7,26.9,19.3,28.3,17.5,28.3z M26.4,37.8h-2.3c-0.8,0-1.5-0.7-1.5-1.5c0-0.8,0.7-1.5,1.5-1.5h2.3c0.8,0,1.5,0.7,1.5,1.5C27.9,37.2,27.2,37.8,26.4,37.8z M33.8,37.8h-2.3c-0.8,0-1.5-0.7-1.5-1.5c0-0.8,0.7-1.5,1.5-1.5h2.3c0.8,0,1.5,0.7,1.5,1.5C35.3,37.2,34.6,37.8,33.8,37.8z M32.5,28.3c-1.8,0-3.2-1.4-3.2-3.2c0-1.8,1.4-3.2,3.2-3.2c1.8,0,3.2,1.4,3.2,3.2C35.7,26.9,34.3,28.3,32.5,28.3z" />
              </svg>
            </a>
            <div className="dropdown" style={{ cursor: "pointer" }}>
              <span className="menu-ico">
                <svg
                  version="1.1"
                  x="0px"
                  y="0px"
                  viewBox="0 0 50 50"
                  xmlSpace="preserve"
                >
                  <path d="M49.8,17.2c-0.2-1.1-0.8-2.1-1.5-2.9L43,4L6.6,4.4l-5,9.9c-0.8,0.9-1.3,2-1.5,3.2C0.1,17.7,0,17.8,0,18c0,0.2,0,0.4,0,0.6c0,3.6,3.1,6.6,7,6.6c2.4,0,4.6-1.2,5.8-3c1.2,2,3.5,3.4,6.1,3.4c2.7,0,5.1-1.5,6.2-3.6c1.2,1.9,3.4,3.2,6,3.2c2.5,0,4.7-1.3,5.9-3.1c1.2,1.9,3.4,3.1,5.9,3.1c3.8,0,6.9-2.9,7-6.5C50.1,18.2,50,17.7,49.8,17.2z" />
                  <path d="M40.6,35.7H9.4v-8.2H4.2v15.7C4.2,44.7,5.4,46,7,46h35.4c1.9,0,3.4-1.5,3.4-3.4V27.5h-5.2V35.7z" />
                </svg>
              </span>
              <div
                className="dropdown-content dropdown-menu-end"
                style={{
                  minWidth: 250,
                  rowGap: "0px !important",
                  borderRadius: 20,
                  right: 0,
                  top: "100%"
                }}
              >
                <div className="mb-2">
                  <a
                    href="https://aicpanel.smsit.ai/ecommerce"
                    className="navbaar--list-link"
                  >
                    <img
                      src="https://aicpanel.smsit.ai/assets/img/ecommerce-icons/LeftNavIcon-133.png"
                      width="15%"
                    />
                    <span>Ecommerce</span>
                  </a>
                </div>
                <div>
                  <a
                    href="https://aicpanel.smsit.ai/redirect-smarttools/commerce-smsit-ai"
                    className="navbaar--list-link"
                  >
                    <img
                      src="https://aicpanel.smsit.ai/assets/img/settings-icons/LeftNavIcon-105.png"
                      width="15%"
                    />
                    <span>Commerce</span>
                  </a>
                </div>
              </div>
            </div>
            <div ref={quickLinkRef} className="dropdown-center dropdown-quick-links">
              <button
                onClick={() => setShowQuickLink(!showQuickLink)}
                style={{ cursor: "pointer" }}
                className=" dropdown-toggle top-navbaar-icons m-0 header-main-menu top-nav-drop-icon" type="button" data-bs-toggle="dropdown" data-bs-auto-close="outside" aria-expanded="false" >
                <div className="w-100" data-bs-toggle="tooltip" data-bs-title="Quick Links" data-bs-placement="bottom" >
                  <svg
                    version="1.1"
                    x="0px"
                    y="0px"
                    viewBox="0 0 50 50"
                    xmlSpace="preserve"
                  >
                    <path d="M25,0C11.2,0,0,11.2,0,25s11.2,25,25,25s25-11.2,25-25S38.8,0,25,0z M33.1,26.7h-6.4v6.4c0,0.9-0.7,1.7-1.7,1.7s-1.7-0.7-1.7-1.7v-6.4h-6.4c-0.9,0-1.7-0.7-1.7-1.7c0-0.9,0.7-1.7,1.7-1.7h6.4v-6.4c0-0.9,0.7-1.7,1.7-1.7s1.7,0.7,1.7,1.7v6.4h6.4c0.9,0,1.7,0.7,1.7,1.7C34.7,25.9,34,26.7,33.1,26.7z" />
                  </svg>
                </div>
              </button>
              {
                showQuickLink &&
                <div className="p-0 dropdown-menu-dots-icon dropdown-menu-end" style={{ width: 300 }} >
                  <div className="navbaar-menu">
                    <div className="navbaar-menu-social mt-3">
                      <div className="navbaar-menu-account--list">
                        <form id="menuDragListForm">
                          <ul className="menuDragList ui-sortable">
                            <li className="ui-sortable-handle">
                              <a href="https://aicpanel.smsit.ai/users/active-phone">
                                <div className="flex align-center">
                                  <div className="menuDragList-icon">
                                    <img
                                      className=""
                                      src="https://aicpanel.smsit.ai/assets/img/nav-icon/icon-19.png"
                                      alt="icon"
                                      width="32px"
                                    />
                                  </div>
                                  <input
                                    type="hidden"
                                    name="menuDragListId[]"
                                    defaultValue={19}
                                  />
                                  <div className="menuDragList-text">Active Numbers</div>
                                </div>
                              </a>
                            </li>
                            <li className="ui-sortable-handle">
                              <a href="https://aicpanel.smsit.ai/campaigns">
                                <div className="flex align-center">
                                  <div className="menuDragList-icon">
                                    <img
                                      className=""
                                      src="https://aicpanel.smsit.ai/assets/img/nav-icon/icon-7.png"
                                      alt="icon"
                                      width="32px"
                                    />
                                  </div>
                                  <input
                                    type="hidden"
                                    name="menuDragListId[]"
                                    defaultValue={7}
                                  />
                                  <div className="menuDragList-text">Campaigns</div>
                                </div>
                              </a>
                            </li>
                            <li className="ui-sortable-handle">
                              <a href="https://aicpanel.smsit.ai/contacts/index">
                                <div className="flex align-center">
                                  <div className="menuDragList-icon">
                                    <img
                                      className=""
                                      src="https://aicpanel.smsit.ai/assets/img/nav-icon/icon-18.png"
                                      alt="icon"
                                      width="32px"
                                    />
                                  </div>
                                  <input
                                    type="hidden"
                                    name="menuDragListId[]"
                                    defaultValue={18}
                                  />
                                  <div className="menuDragList-text">Contacts</div>
                                </div>
                              </a>
                            </li>
                            <li className="ui-sortable-handle">
                              <a href="https://aicpanel.smsit.ai/voice/dialer">
                                <div className="flex align-center">
                                  <div className="menuDragList-icon">
                                    <img
                                      className=""
                                      src="https://aicpanel.smsit.ai/assets/img/nav-icon/icon-2.png"
                                      alt="icon"
                                      width="32px"
                                    />
                                  </div>
                                  <input
                                    type="hidden"
                                    name="menuDragListId[]"
                                    defaultValue={2}
                                  />
                                  <div className="menuDragList-text">Make a Call</div>
                                </div>
                              </a>
                            </li>
                            <li
                              className="ui-sortable-handle"
                            >
                              <div className="flex align-center">
                                <div className="menuDragList-icon">
                                  <img
                                    className=""
                                    src="https://aicpanel.smsit.ai/assets/img/nav-icon/icon-1.png"
                                    alt="icon"
                                    width="32px"
                                  />
                                </div>
                                <input
                                  type="hidden"
                                  name="menuDragListId[]"
                                  defaultValue={1}
                                />
                                <div className="menuDragList-text">Send Text</div>
                              </div>
                            </li>
                            <li className="ui-sortable-handle">
                              <a href="https://aicpanel.smsit.ai/messages">
                                <div className="flex align-center">
                                  <div className="menuDragList-icon">
                                    <img
                                      className=""
                                      src="https://aicpanel.smsit.ai/assets/img/nav-icon/icon-9.png"
                                      alt="icon"
                                      width="32px"
                                    />
                                  </div>
                                  <input
                                    type="hidden"
                                    name="menuDragListId[]"
                                    defaultValue={9}
                                  />
                                  <div className="menuDragList-text">Bulk Text</div>
                                </div>
                              </a>
                            </li>
                            <li className="ui-sortable-handle">
                              <a href="https://aicpanel.smsit.ai/smartinbox">
                                <div className="flex align-center">
                                  <div className="menuDragList-icon">
                                    <img
                                      className=""
                                      src="https://aicpanel.smsit.ai/assets/img/nav-icon/icon-8.png"
                                      alt="icon"
                                      width="32px"
                                    />
                                  </div>
                                  <input
                                    type="hidden"
                                    name="menuDragListId[]"
                                    defaultValue={8}
                                  />
                                  <div className="menuDragList-text">Smart Inbox</div>
                                </div>
                              </a>
                            </li>
                            <li className="ui-sortable-handle">
                              <a href="https://aicpanel.smsit.ai/emailchats">
                                <div className="flex align-center">
                                  <div className="menuDragList-icon">
                                    <img
                                      className=""
                                      src="https://aicpanel.smsit.ai/assets/img/nav-icon/icon-11.png"
                                      alt="icon"
                                      width="32px"
                                    />
                                  </div>
                                  <input
                                    type="hidden"
                                    name="menuDragListId[]"
                                    defaultValue={11}
                                  />
                                  <div className="menuDragList-text">Email</div>
                                </div>
                              </a>
                            </li>
                            <li
                              className="ui-sortable-handle"
                            >
                              <div className="flex align-center">
                                <div className="menuDragList-icon">
                                  <img
                                    className=""
                                    src="https://aicpanel.smsit.ai/assets/img/nav-icon/icon-3.png"
                                    alt="icon"
                                    width="32px"
                                  />
                                </div>
                                <input
                                  type="hidden"
                                  name="menuDragListId[]"
                                  defaultValue={3}
                                />
                                <div className="menuDragList-text">Add a Contact</div>
                              </div>
                            </li>
                            <li className="ui-sortable-handle">
                              <a href="https://aicpanel.smsit.ai/voice">
                                <div className="flex align-center">
                                  <div className="menuDragList-icon">
                                    <img
                                      className=""
                                      src="https://aicpanel.smsit.ai/assets/img/nav-icon/icon-10.png"
                                      alt="icon"
                                      width="32px"
                                    />
                                  </div>
                                  <input
                                    type="hidden"
                                    name="menuDragListId[]"
                                    defaultValue={10}
                                  />
                                  <div className="menuDragList-text">Voice</div>
                                </div>
                              </a>
                            </li>
                            <li
                              className="ui-sortable-handle"
                            >
                              <div className="flex align-center">
                                <div className="menuDragList-icon">
                                  <img
                                    className=""
                                    src="https://aicpanel.smsit.ai/assets/img/nav-icon/icon-4.png"
                                    alt="icon"
                                    width="32px"
                                  />
                                </div>
                                <input
                                  type="hidden"
                                  name="menuDragListId[]"
                                  defaultValue={4}
                                />
                                <div className="menuDragList-text">Add a Group</div>
                              </div>
                            </li>
                            <li className="ui-sortable-handle">
                              <a href="https://aicpanel.smsit.ai/appointment/index">
                                <div className="flex align-center">
                                  <div className="menuDragList-icon">
                                    <img
                                      className=""
                                      src="https://aicpanel.smsit.ai/assets/img/nav-icon/icon-13.png"
                                      alt="icon"
                                      width="32px"
                                    />
                                  </div>
                                  <input
                                    type="hidden"
                                    name="menuDragListId[]"
                                    defaultValue={13}
                                  />
                                  <div className="menuDragList-text">Appointments</div>
                                </div>
                              </a>
                            </li>
                            <li
                              className="ui-sortable-handle"
                            >
                              <div className="flex align-center">
                                <div className="menuDragList-icon">
                                  <img
                                    className=""
                                    src="https://aicpanel.smsit.ai/assets/img/nav-icon/icon-6.png"
                                    alt="icon"
                                    width="32px"
                                  />
                                </div>
                                <input
                                  type="hidden"
                                  name="menuDragListId[]"
                                  defaultValue={6}
                                />
                                <div className="menuDragList-text">Create a Task</div>
                              </div>
                            </li>
                            <li className="ui-sortable-handle">
                              <a href="https://aicpanel.smsit.ai/users/setting">
                                <div className="flex align-center">
                                  <div className="menuDragList-icon">
                                    <img
                                      className=""
                                      src="https://aicpanel.smsit.ai/assets/img/nav-icon/icon-20.png"
                                      alt="icon"
                                      width="32px"
                                    />
                                  </div>
                                  <input
                                    type="hidden"
                                    name="menuDragListId[]"
                                    defaultValue={20}
                                  />
                                  <div className="menuDragList-text">
                                    Account Settings
                                  </div>
                                </div>
                              </a>
                            </li>
                            <li className="ui-sortable-handle">
                              <a href="https://aicpanel.smsit.ai/logs">
                                <div className="flex align-center">
                                  <div className="menuDragList-icon">
                                    <img
                                      className=""
                                      src="https://aicpanel.smsit.ai/assets/img/nav-icon/icon-21.png"
                                      alt="icon"
                                      width="32px"
                                    />
                                  </div>
                                  <input
                                    type="hidden"
                                    name="menuDragListId[]"
                                    defaultValue={21}
                                  />
                                  <div className="menuDragList-text">Reports</div>
                                </div>
                              </a>
                            </li>
                            <li className="ui-sortable-handle">
                              <a href="https://aicpanel.smsit.ai/lead-management">
                                <div className="flex align-center">
                                  <div className="menuDragList-icon">
                                    <img
                                      className=""
                                      src="https://aicpanel.smsit.ai/assets/img/nav-icon/icon-22.png"
                                      alt="icon"
                                      width="32px"
                                    />
                                  </div>
                                  <input
                                    type="hidden"
                                    name="menuDragListId[]"
                                    defaultValue={22}
                                  />
                                  <div className="menuDragList-text">Lead Management</div>
                                </div>
                              </a>
                            </li>
                            <li
                              className="ui-sortable-handle"
                            >
                              <div className="flex align-center">
                                <div className="menuDragList-icon">
                                  <img
                                    className=""
                                    src="https://aicpanel.smsit.ai/assets/img/nav-icon/icon-16.png"
                                    alt="icon"
                                    width="32px"
                                  />
                                </div>
                                <input
                                  type="hidden"
                                  name="menuDragListId[]"
                                  defaultValue={16}
                                />
                                <div className="menuDragList-text">Train AI</div>
                              </div>
                            </li>
                            <li className="ui-sortable-handle">
                              <a href="https://aicpanel.smsit.ai/pipeline/opportunities/all/25">
                                <div className="flex align-center">
                                  <div className="menuDragList-icon">
                                    <img
                                      className=""
                                      src="https://aicpanel.smsit.ai/assets/img/nav-icon/icon-23.png"
                                      alt="icon"
                                      width="32px"
                                    />
                                  </div>
                                  <input
                                    type="hidden"
                                    name="menuDragListId[]"
                                    defaultValue={23}
                                  />
                                  <div className="menuDragList-text">Opportunities</div>
                                </div>
                              </a>
                            </li>
                            <li
                              className="ui-sortable-handle"
                            >
                              <div className="flex align-center">
                                <div className="menuDragList-icon">
                                  <img
                                    className=""
                                    src="https://aicpanel.smsit.ai/assets/img/nav-icon/icon-5.png"
                                    alt="icon"
                                    width="32px"
                                  />
                                </div>
                                <input
                                  type="hidden"
                                  name="menuDragListId[]"
                                  defaultValue={5}
                                />
                                <div className="menuDragList-text">Add a Tag</div>
                              </div>
                            </li>
                            <li className="ui-sortable-handle">
                              <a href="https://aicpanel.smsit.ai/messages/view">
                                <div className="flex align-center">
                                  <div className="menuDragList-icon">
                                    <img
                                      className=""
                                      src="https://aicpanel.smsit.ai/assets/img/nav-icon/icon-14.png"
                                      alt="icon"
                                      width="32px"
                                    />
                                  </div>
                                  <input
                                    type="hidden"
                                    name="menuDragListId[]"
                                    defaultValue={14}
                                  />
                                  <div className="menuDragList-text">
                                    Schedule Calendar
                                  </div>
                                </div>
                              </a>
                            </li>
                            <li className="ui-sortable-handle">
                              <a href="https://aicpanel.smsit.ai/notepad/all">
                                <div className="flex align-center">
                                  <div className="menuDragList-icon">
                                    <img
                                      className=""
                                      src="https://aicpanel.smsit.ai/assets/img/nav-icon/icon-12.png"
                                      alt="icon"
                                      width="32px"
                                    />
                                  </div>
                                  <input
                                    type="hidden"
                                    name="menuDragListId[]"
                                    defaultValue={12}
                                  />
                                  <div className="menuDragList-text">Notepad</div>
                                </div>
                              </a>
                            </li>
                            <li className="ui-sortable-handle">
                              <a href="https://aicpanel.smsit.ai/smart-tool">
                                <div className="flex align-center">
                                  <div className="menuDragList-icon">
                                    <img
                                      className=""
                                      src="https://aicpanel.smsit.ai/assets/img/nav-icon/icon-15.png"
                                      alt="icon"
                                      width="32px"
                                    />
                                  </div>
                                  <input
                                    type="hidden"
                                    name="menuDragListId[]"
                                    defaultValue={15}
                                  />
                                  <div className="menuDragList-text">Smart Tools</div>
                                </div>
                              </a>
                            </li>
                            <li className="ui-sortable-handle">
                              <a href="https://aicpanel.smsit.ai/pipelines">
                                <div className="flex align-center">
                                  <div className="menuDragList-icon">
                                    <img
                                      className=""
                                      src="https://aicpanel.smsit.ai/assets/img/nav-icon/icon-17.png"
                                      alt="icon"
                                      width="32px"
                                    />
                                  </div>
                                  <input
                                    type="hidden"
                                    name="menuDragListId[]"
                                    defaultValue={17}
                                  />
                                  <div className="menuDragList-text">Pipelines</div>
                                </div>
                              </a>
                            </li>
                          </ul>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              }
            </div>
          </div>
        </div>
        <div className="user-menu">
          <DesktopMenu position={menuPosition} />
          {(location.pathname === '/dashboard' || location.pathname.startsWith('/dashboard/')) && (
            <button className='back-btn'>
              <a href="/admin">
                <svg
                  version="1.1"
                  x="0px"
                  y="0px"
                  viewBox="0 0 25 25"
                  xmlSpace="preserve"
                >
                  <path
                    d="M16.4,1.9H1.6C0.7,1.9,0,2.6,0,3.5S0.7,5,1.6,5h14.8c3,0,5.5,2.5,5.5,5.5s-2.5,5.5-5.5,5.5h-2.6l1.2-1.2
                    c0.6-0.6,0.6-1.6,0-2.2s-1.6-0.6-2.2,0l-3.9,3.9c-0.6,0.6-0.6,1.6,0,2.2l3.9,3.9c0.3,0.3,0.7,0.5,1.1,0.5s0.8-0.2,1.1-0.5
                    c0.6-0.6,0.6-1.6,0-2.2l-1.2-1.2h2.6c4.8,0,8.6-3.9,8.6-8.6S21.1,1.9,16.4,1.9z"
                    fill={'#f00'}
                  />
                </svg>
              </a>
            </button>
          )}
        </div>

        <button onClick={() => setShowActive(!showActive)} id="headericonlistBtn" className="btn header-iconlist-btn ms-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="feather feather-chevron-right"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>

        <div className={`top-navbaar-icon-list lg:ms-auto ${showActive ? "active" : ''}`}>
          <div ref={menuRef} className="flex align-center justify-content-center top-navbaar-icons text-center">
            <div className="dropdown-center">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="dropdown-toggle top-navbaar-icons m-0 header-main-menu top-nav-drop-icon" type="button" data-bs-toggle="dropdown" data-bs-auto-close="outside" aria-expanded="false">
                <svg
                  version="1.1"
                  x="0px"
                  y="0px"
                  viewBox="0 0 50 50"
                  xmlSpace="preserve"
                >
                  <circle cx="6.2" cy="6.5" r="6.2" />
                  <circle cx="24.8" cy="6.5" r="6.2" />
                  <circle cx="43.8" cy="6.5" r="6.2" />
                  <circle cx="6.2" cy="25.3" r="6.2" />
                  <circle cx="24.8" cy="25.3" r="6.2" />
                  <circle cx="43.8" cy="25.3" r="6.2" />
                  <circle cx="6.2" cy="43.5" r="6.2" />
                  <circle cx="24.8" cy="43.5" r="6.2" />
                  <circle cx="43.8" cy="43.5" r="6.2" />
                </svg>

              </button>
              {showMenu &&
                <div className="dropdown-menu dropdown-menu-dots-icon dropdown-menu-end">
                  <div className="navbaar-menu">
                    <h4 style={{ fontWeight: 600, textAlign: "left" }}>Menu</h4>
                    <div className="row navbaar-menu-social mt-3">
                      <div className="col-md-6 navbaar-menu-account--list">
                        <div className="row">
                          <div className="col-md-12">
                            <h6 style={{ fontWeight: 600, marginLeft: "15px" }}>My Account</h6>
                          </div>
                        </div>
                        <div className="row mb-2 mt-3 navbaar-menu-account--list--item">
                          <div className="col-md-12">
                            <a href="https://aicpanel.smsit.ai/users/account">
                              <img src="https://aicpanel.smsit.ai/assets/img/navbar/accounts.png" width="30px" />
                              <span>Profile</span>
                            </a>
                          </div>
                        </div>
                        <div className="row mb-2 mt-3 navbaar-menu-account--list--item">
                          <div className="col-md-12">
                            <a href="https://aicpanel.smsit.ai/users/active-phone">
                              <img src="https://aicpanel.smsit.ai/assets/img/navbar/numbers.png" width="30px" />
                              <span>Numbers</span>
                            </a>
                          </div>
                        </div>
                        <div className="row mb-2 mt-3 navbaar-menu-account--list--item">
                          <div className="col-md-12">
                            <a href="https://aicpanel.smsit.ai/users/gateway_settings">
                              <img src="https://aicpanel.smsit.ai/assets/img/navbar/sms-gateway-and-smpp.png" width="30px" />
                              <span>SMS Gateways &amp; SMPP</span>
                            </a>
                          </div>
                        </div>
                        <div className="row mb-2 navbaar-menu-account--list--item">
                          <div className="col-md-12">
                            <a href="#null">
                              <img src="https://aicpanel.smsit.ai/assets/img/navbar/billing.png" width="30px" />
                              <span>Billing</span>
                            </a>
                          </div>
                        </div>
                        <div className="row mb-2 navbaar-menu-account--list--item">
                          <div className="col-md-12">
                            <a href="https://aicpanel.smsit.ai/users/setting">
                              <img src="https://aicpanel.smsit.ai/assets/img/navbar/settings.png" width="30px" />
                              <span>Settings</span>
                            </a>
                          </div>
                        </div>
                        <div className="row mb-2 navbaar-menu-account--list--item">
                          <div className="col-md-12">
                            <a href="https://aicpanel.smsit.ai/users/subaccounts">
                              <img src="https://aicpanel.smsit.ai/assets/img/navbar/team-sub.png" width="30px" />
                              <span>Team Subaccounts</span>
                            </a>
                          </div>
                        </div>
                        <div className="row mb-2 navbaar-menu-account--list--item">
                          <div className="col-md-12">
                            <a href="https://aicpanel.smsit.ai/agency/accounts">
                              <img src="https://aicpanel.smsit.ai/assets/img/navbar/agency-sub.png" width="30px" />

                              <span>Reseller Client Accounts</span>
                            </a>
                          </div>
                        </div>
                        <div className="row mb-2 navbaar-menu-account--list--item">
                          <div className="col-md-12">
                            <a href="https://aicpanel.smsit.ai/affiliates">
                              <img src="https://aicpanel.smsit.ai/assets/img/navbar/affiliates.png" width="30px" />
                              <span>Affiliates</span>
                            </a>
                          </div>
                        </div>
                        <div className="row mb-2 navbaar-menu-account--list--item">
                          <div className="col-md-12">
                            <a href="https://aicpanel.smsit.ai/users/smtp">
                              <img src="https://aicpanel.smsit.ai/assets/img/navbar/email-services.png" width="30px" />
                              <span>SMTP Service</span>
                            </a>
                          </div>
                        </div>
                        <div className="row mb-2 navbaar-menu-account--list--item">
                          <div className="col-md-12">
                            <a href="https://aicpanel.smsit.ai/users/integration">
                              <img src="https://aicpanel.smsit.ai/assets/img/navbar/integrations.png" width="30px" />
                              <span>Integrations</span>
                            </a>
                          </div>
                        </div>
                        <div className="row mb-2 navbaar-menu-account--list--item">
                          <div className="col-md-12">
                            <a href="#null">
                              <img src="https://aicpanel.smsit.ai/assets/img/navbar/10dlc.png" width="30px" />
                              <span>10 DLC</span>
                            </a>
                          </div>
                        </div>
                        <div className="row mb-2 navbaar-menu-account--list--item">
                          <div className="col-md-12">
                            <a href="#null">
                              <img src="https://aicpanel.smsit.ai/assets/img/navbar/favs.png" width="30px" />
                              <span>Favs</span>
                            </a>
                          </div>
                        </div>
                        <div className="row mb-2 navbaar-menu-account--list--item">
                          <div className="col-md-12">
                            <a href="#null">
                              <img src="https://aicpanel.smsit.ai/assets/img/navbar/pricing.png" width="30px" />
                              <span>Pricing</span>
                            </a>
                          </div>
                        </div>
                        <hr />
                        <div className="row mt-3 mb-3">
                          <div className="col-md-12">
                            <h6 style={{ fontWeight: 600 }}>Learn</h6>
                          </div>
                        </div>
                        <div className="row mb-2 navbaar-menu-account--list--item">
                          <div className="col-md-12">
                            <a href="https://university.smsit.ai/">
                              <img src="https://aicpanel.smsit.ai/assets/img/navbar/university.png" width="30px" />
                              <span>University</span>
                            </a>
                          </div>
                        </div>
                        <div className="row mb-2 navbaar-menu-account--list--item">
                          <div className="col-md-12">
                            <a href="https://training.smsit.ai/">
                              <img src="https://aicpanel.smsit.ai/assets/img/navbar/knowledge-base.png" width="30px" />
                              <span>Knowledge Base</span>
                            </a>
                          </div>
                        </div>
                        <hr />
                        <div className="row mt-3 mb-3">
                          <div className="col-md-12">
                            <h6 style={{ fontWeight: 600 }}>Social</h6>
                          </div>
                        </div>
                        <div className="row mb-2 navbaar-menu-account--list--item">
                          <div className="col-md-12">
                            <a href="https://blog.smsit.ai" target="_blank">
                              <img src="https://aicpanel.smsit.ai/assets/img/navbar/blog.png" width="30px" />
                              <span>Blog</span>
                            </a>
                          </div>
                        </div>
                        <div className="row mb-2 navbaar-menu-account--list--item">
                          <div className="col-md-12">
                            <a href="https://www.facebook.com/groups/778896976627147/" target="_blank">
                              <img src="https://aicpanel.smsit.ai/assets/img/navbar/facebook.png" width="30px" />
                              <span>Facebook Group</span>
                            </a>
                          </div>
                        </div>
                        <div className="row mb-2 navbaar-menu-account--list--item">
                          <div className="col-md-12">
                            <a href="https://www.youtube.com/channel/UCGo4bBea1YT2WVct2qOWfFQ" target="_blank">
                              <img src="https://aicpanel.smsit.ai/assets/img/navbar/youtube.png" width="30px" />
                              <span>Youtube Channel</span>
                            </a>
                          </div>
                        </div>
                        <div className="row mb-2 navbaar-menu-account--list--item">
                          <div className="col-md-12">
                            <a href="https://discord.gg/3572fYU67Z" target="_blank">
                              <img src="https://aicpanel.smsit.ai/assets/img/navbar/discord.png" width="30px" />
                              <span>Discord</span>
                            </a>
                          </div>
                        </div>
                        <div className="row mb-2 navbaar-menu-account--list--item">
                          <div className="col-md-12">
                            <a href="https://www.twitter.com/smsit_ai" target="_blank">
                              <img src="https://aicpanel.smsit.ai/assets/img/navbar/twitter.png" width="30px" />
                              <span>Twitter</span>
                            </a>
                          </div>
                        </div>
                        <div className="row mb-2 navbaar-menu-account--list--item">
                          <div className="col-md-12">
                            <a href="https://www.linkedin.com/company/sms-it" target="_blank">
                              <img src="https://aicpanel.smsit.ai/assets/img/navbar/linkedin.png" width="30px" />
                              <span>LinkedIn</span>
                            </a>
                          </div>
                        </div>
                        <div className="row mb-2 navbaar-menu-account--list--item">
                          <div className="col-md-12">
                            <a href="https://www.instagram.com/smsitai">
                              <img src="https://aicpanel.smsit.ai/assets/img/navbar/instagram.png" width="30px" />
                              <span>Instagram</span>
                            </a>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-5 navbaar-menu-social--create" style={{ overflowY: "scroll", height: "500px", overflowX: "hidden" }}>
                        <div className="row align-items-center">
                          <div className="col-md-12">
                            <h6 style={{ fontWeight: 600 }}>Create</h6>
                          </div>
                          <div className="row mb-2 mt-3 justify-content-start navbaar-menu-account--list--item">
                            <div className="col-md-12">
                              <a href="https://aicpanel.smsit.ai/messages">
                                <img src="https://aicpanel.smsit.ai/assets/img/navbar/text.png" width="30px" />
                                <span>Text</span>
                              </a>
                            </div>
                          </div>
                          <div className="row mb-2 justify-content-start navbaar-menu-account--list--item">
                            <div className="col-md-12">
                              <a href="https://aicpanel.smsit.ai/emailchats">
                                <img src="https://aicpanel.smsit.ai/assets/img/navbar/email.png" width="30px" />
                                <span>Email</span>
                              </a>
                            </div>
                          </div>
                          <div className="row mb-2 justify-content-start navbaar-menu-account--list--item">
                            <div className="col-md-12">
                              <a href="https://aicpanel.smsit.ai/voice">
                                <img src="https://aicpanel.smsit.ai/assets/img/navbar/voice.png" width="30px" />
                                <span>Voice</span>
                              </a>
                            </div>
                          </div>
                          <div className="row mb-2 justify-content-start navbaar-menu-account--list--item">
                            <div className="col-md-12">
                              <a href="https://aicpanel.smsit.ai/voice/dialer">
                                <img src="https://aicpanel.smsit.ai/assets/img/navbar/call.png" width="30px" />
                                <span>Call</span>
                              </a>
                            </div>
                          </div>
                          <div className="row mb-2 justify-content-start navbaar-menu-account--list--item">
                            <div className="col-md-12">
                              <a href="https://aicpanel.smsit.ai/faxchats">
                                <img src="https://aicpanel.smsit.ai/assets/img/navbar/fax.png" width="30px" />
                                <span>Fax</span>
                              </a>
                            </div>
                          </div>
                          <hr />
                          <div className="row mb-2 mt-3 justify-content-start navbaar-menu-account--list--item">
                            <div className="col-md-12">
                              <a href="https://aicpanel.smsit.ai/appointment/index">
                                <img src="https://aicpanel.smsit.ai/assets/img/navbar/appointment.png" width="30px" />
                                <span>Appointment</span>
                              </a>
                            </div>
                          </div>
                          <div className="row mb-2 justify-content-start navbaar-menu-account--list--item">
                            <div className="col-md-12">
                              <a href="https://aicpanel.smsit.ai/groups">
                                <img src="https://aicpanel.smsit.ai/assets/img/navbar/groups.png" width="30px" />
                                <span>Group<br />(Keyword)</span>
                              </a>
                            </div>
                          </div>
                          <div className="row mb-2 justify-content-start navbaar-menu-account--list--item">
                            <div className="col-md-12">
                              <a href="https://aicpanel.smsit.ai/contacts/index">
                                <img src="https://aicpanel.smsit.ai/assets/img/navbar/contacts.png" width="30px" />
                                <span>Contact</span>
                              </a>
                            </div>
                          </div>
                          <div className="row mb-2 justify-content-start navbaar-menu-account--list--item">
                            <div className="col-md-12">
                              <a href="https://aicpanel.smsit.ai/templates">
                                <img src="https://aicpanel.smsit.ai/assets/img/navbar/templates.png" width="30px" />
                                <span>Templates</span>
                              </a>
                            </div>
                          </div>
                          <div className="row mb-2 justify-content-start navbaar-menu-account--list--item">
                            <div className="col-md-12">
                              <a href="https://aicpanel.smsit.ai/tags">
                                <img src="https://aicpanel.smsit.ai/assets/img/navbar/tags.png" width="30px" />
                                <span>Tags</span>
                              </a>
                            </div>
                          </div>
                          <div className="row mb-2 justify-content-start navbaar-menu-account--list--item">
                            <div className="col-md-12">
                              <a href="https://aicpanel.smsit.ai/pipelines">
                                <img src="https://aicpanel.smsit.ai/assets/img/navbar/pipelines.png" width="30px" />
                                <span>Pipelines</span>
                              </a>
                            </div>
                          </div>
                          <hr />
                          <div className="row mb-2 mt-3 justify-content-start navbaar-menu-account--list--item">
                            <div className="col-md-12">
                              <a href="https://aicpanel.smsit.ai/smart-tool">
                                <img src="https://aicpanel.smsit.ai/assets/img/navbar/smart-tools.png" width="30px" />
                                <span>Smart Tools</span>
                              </a>
                            </div>
                          </div>
                          <div className="row mb-2 justify-content-start navbaar-menu-account--list--item">
                            <div className="col-md-12">
                              <a href="https://aicpanel.smsit.ai/redirect-workflow/ai-insights">
                                <img src="https://aicpanel.smsit.ai/assets/img/navbar/workflow-automation.png" width="30px" />
                                <span>Workflow <br /> Automation</span>
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              }

            </div>
          </div>
          <div ref={messageRef} className="flex align-center justify-content-center top-navbaar-icons text-center">
            <div className="dropdown-center">
              <button
                onClick={() => setShowMessage(!showMessage)}
                className="dropdown-toggle top-navbaar-icons top-nav-drop-icon m-0" id="smartbox-message-dropdown" type="button" data-bs-toggle="dropdown" data-bs-auto-close="outside" aria-expanded="false">
                <span className="fa-stack" data-count="0" id="notification-icon">
                  <svg
                    version="1.1"
                    x="0px"
                    y="0px"
                    viewBox="0 0 50 50"
                    xmlSpace="preserve"
                  >
                    <path d="M43.4,0H6.6C3,0,0,3,0,6.7v27.7c0,3.7,3,6.7,6.6,6.7h9v6.9c0,1.6,1.9,2.5,3.1,1.5l10.4-8.5h14.3c3.7,0,6.6-3,6.6-6.7V6.7C50,3,47,0,43.4,0z" />
                  </svg>

                </span>
              </button>
              {showMessage &&
                <div className="dropdown-menu chat-drop-area dropdown-menu-end" id="message-dropdown-menu">
                  <div className="message-content-body-main-left" style={{ width: "100%" }}>
                    <div className="message-content-body-header">
                      <p style={{ fontWeight: 600, fontSize: "larger" }}>Chats</p>
                      <div className="message-content-body-header-icons">
                        <div ref={chatRef} className="dropstart">
                          <button onClick={() => setShowChatSetting(!showChatSetting)} className="dropdown-toggle chat-settings-btn" type="button" data-bs-toggle="dropdown" data-bs-auto-close="outside" aria-expanded="false">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="feather feather-more-horizontal"
                            >
                              <circle cx="12" cy="12" r="1" />
                              <circle cx="19" cy="12" r="1" />
                              <circle cx="5" cy="12" r="1" />
                            </svg>
                          </button>
                          {showChatSetting &&
                            <div className="dropdown-menu drop-nav-chat-inner-setting" style={{ width: "300px" }}>
                              <div className="chat-settings-main">
                                <div className="chat-settings-header">
                                  <p style={{ fontWeight: 600 }}>Chat Settings</p>
                                  <p style={{ fontSize: "12px", opacity: 0.7 }}>Customize your messenger experience</p>
                                </div>
                                <hr />
                                <div className="chat-settings-main-toggle">
                                  <div className="chat-settings-toggle-left">
                                    <i className="fa-solid fa-volume-low" aria-hidden="true"></i>
                                    <p>Message Sounds</p>
                                  </div>
                                  <label className="switch">
                                    <input type="checkbox" id="number-pool-checkbox" />
                                    <span className="slider round" style={{ backgroundColor: "#ccc", color: "#22B573" }}></span>
                                  </label>
                                </div>
                              </div>
                            </div>
                          }
                        </div>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="feather feather-edit"
                          id="send-header-contact-sms"
                        >
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>

                      </div>
                    </div>
                    <div className="message-content-body-inbox-search-box align-items-center" style={{ visibility: "hidden", height: 0 }} >
                      <span>
                        <i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
                      </span>
                      <input type="text" placeholder="Search" />
                    </div>
                    <div id="sms_inbox_chat" className="chatbox-contacts-list chatbox-contacts-list-nav header-chatbox-contacts-list">
                      <div id="smartboxloader"></div>
                      <div id="sms-smartbox-contactlists">
                      </div>
                    </div>
                  </div>
                </div>
              }

            </div>
          </div>
          <div ref={faxRef} className="flex align-center justify-content-center top-navbaar-icons text-center">
            <div className="dropdown-center">
              <button
                onClick={() => setShowFax(!showFax)}
                className="dropdown-toggle top-navbaar-icons top-nav-drop-icon m-0" id="message-fax-dropdown" type="button" data-bs-toggle="dropdown" data-bs-auto-close="outside" aria-expanded="false">
                <span className="fa-stack" data-count="0" id="fax-notification-icon">
                  <svg
                    version="1.1"
                    x="0px"
                    y="0px"
                    viewBox="0 0 50 50"
                    xmlSpace="preserve"
                  >
                    <g>
                      <path d="M12.5,6.8h21.6c0,0,3.3,0.6,3.3,3.3v6h5.3V7.3c0,0-2.1-6.7-8.8-6.7H11.4c0,0-4.2,1.6-4.2,6.4v9.1h5.3V6.8z" />
                      <path d="M43.9,18.2H6.1C2.7,18.2,0,21,0,24.3V35c0,1.7,1.4,3,3,3h4.2v5.4c0,3.4,2.7,6.1,6.1,6.1h23.3c3.4,0,6.1-2.7,6.1-6.1V38H47c1.7,0,3-1.4,3-3V24.3C50,21,47.3,18.2,43.9,18.2z M37.3,43.4c0,0.4-0.3,0.8-0.8,0.8H13.3c-0.4,0-0.8-0.3-0.8-0.8V38h24.8V43.4zM42.7,28.9v-0.3h-2c-0.6-0.4-1-1.1-1-1.9c0-1.3,1.1-2.4,2.4-2.4c1.3,0,2.4,1.1,2.4,2.4C44.4,27.7,43.6,28.6,42.7,28.9z" />
                    </g>
                  </svg>
                </span>
              </button>
              {showFax &&
                <div className="dropdown-menu chat-drop-area dropdown-menu-end" id="message-dropdown-menu">
                  <div className="message-content-body-main-left" style={{ width: "100%" }}>
                    <div className="message-content-body-header">
                      <p style={{ fontWeight: 600, fontSize: "larger" }}>Fax</p>
                      <div className="message-content-body-header-icons">
                        {/* <i className="fa-regular fa-pen-to-square" id="header-fax" aria-hidden="true"></i> */}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="feather feather-edit"
                          id="header-fax"
                        >
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </div>
                    </div>
                    <div className="message-content-body-inbox-search-box align-items-center" style={{ visibility: "hidden", height: 0 }}>
                      <span>
                        <i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
                      </span>
                      <input type="text" placeholder="Search" />
                    </div>
                    <div className="chatbox-contacts-list chatbox-contacts-list-nav header-chatbox-contacts-list" id="fax_inbox_chat">
                      <div id="faxloader"></div>
                      <div id="fax-contactlists">
                      </div>
                    </div>
                  </div>
                </div>}
            </div>
          </div>
          <div ref={broadcastRef} className="flex align-center justify-content-center top-navbaar-icons text-center">
            <div className="dropdown-center">
              <button
                onClick={() => setShowBroadcast(!showBroadcast)}
                className="dropdown-toggle top-navbaar-icons top-nav-drop-icon m-0" id="message-broadcast-dropdown" type="button" data-bs-toggle="dropdown" data-bs-auto-close="outside" aria-expanded="false">
                <span className="fa-stack" data-count="0" id="broadcast-notification-icon">
                  <svg version="1.1" x="0px" y="0px" viewBox="0 0 50 50" xmlSpace="preserve">
                    <path d="M46.7,17.9V2.8c-1-5.8-5.2-1.2-5.2-1.2c-6.3,9.9-19,11.4-19,11.4H6.2C0.5,13,0,18,0,18v9.6c0,5.5,6.2,6.8,6.2,6.8v12.5c0,2.4,2.8,3.2,2.8,3.2h6.8c2.7,0,2.8-2.4,2.8-2.4V34.2h3.3c9.6,0,19.6,11.8,19.6,11.8c5,2.8,5.2-2.2,5.2-2.2v-15c1.8-0.2,3.3-2.5,3.3-5.5C50,20.4,48.6,18,46.7,17.9z M40.4,36.6c-6.4-7.2-17.6-7.9-17.6-7.9h-3.9v-9.7h4.5c7.4,0.6,17-7.9,17-7.9V36.6z" />
                  </svg>
                </span>
              </button>
              {showBroadcast &&
                <div className="dropdown-menu chat-drop-area dropdown-menu-end" id="message-dropdown-menu">
                  <div className="message-content-body-main-left" style={{ width: "100%" }}>
                    <div className="message-content-body-header">
                      <p style={{ fontWeight: 600, fontSize: "larger" }}>Broadcast</p>
                      <div className="message-content-body-header-icons">
                        {/* <i className="fa-regular fa-pen-to-square" id="header-broadcast" aria-hidden="true"></i> */}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="feather feather-edit"
                          id="header-broadcast"
                        >
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </div>
                    </div>
                    <div className="message-content-body-inbox-search-box align-items-center" style={{ visibility: "hidden", height: 0 }}>
                      <span>
                        <i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
                      </span>
                      <input type="text" placeholder="Search" />
                    </div>
                    <div className="chatbox-contacts-list chatbox-contacts-list-nav header-chatbox-contacts-list" id="broadcast_inbox_chat">
                      <div id="broadcastloader">
                      </div>
                      <div id="broadcast-contactlists">
                      </div>
                    </div>
                  </div>
                </div>}
            </div>
          </div>
          <div ref={callRef} className="flex align-center justify-content-center top-navbaar-icons text-center">
            <div className="dropdown-center">
              <button
                onClick={() => setShowCall(!showCall)}
                className="dropdown-toggle top-navbaar-icons top-nav-drop-icon m-0" id="message-voice-dropdown" type="button" data-bs-toggle="dropdown" data-bs-auto-close="outside" aria-expanded="false">
                <span className="fa-stack" data-count="0" id="voice-notification-icon">
                  <svg version="1.1" x="0px" y="0px" viewBox="0 0 50 50" xmlSpace="preserve">
                    <path d="M0,12.5c0,2.1,0.8,4.4,1.7,6.8S4,24.1,5,25.8c2.7,4.6,6.1,9.1,10,12.9s8.5,6.9,13.7,8.8c3,1.2,6,2.5,9.3,2.5c1.7,0,4-0.6,6.2-1.5c2.1-0.9,4-2.3,4.7-4c0.3-0.6,0.6-1.5,0.8-2.4c0.2-0.9,0.3-1.8,0.3-2.4c0-0.3,0-0.7-0.1-0.8c-0.2-0.4-0.7-0.8-1.3-1.1c-1.3-0.6-1.5-0.8-3.3-2c-2-1.1-4-2.4-5.7-3.3c-0.8-0.5-1.4-0.7-1.6-0.7c-1.1,0-2.5,1.5-3.7,3.2c-1.3,1.7-2.7,3.2-3.6,3.2c-0.5,0-1-0.2-1.5-0.6S28,37,27.7,36.8c-6.1-3.5-10.3-7.6-13.7-13.7c-0.4-0.7-1.4-2.2-1.4-2.9c0-0.9,1.3-2,2.5-3.1c1.2-1,2.4-2.3,2.4-3.8c0-0.2-0.1-0.8-0.4-1.7c-0.6-1.8-1.5-3.9-2.2-5.9c-0.3-1-0.6-1.7-0.7-2c-0.1-0.2-0.2-0.5-0.3-0.8s-0.2-0.6-0.3-0.9c-0.2-0.6-0.6-1.1-0.9-1.4c-0.3-0.1-1.2-0.3-2-0.4C9.9,0.1,8.9,0,8.5,0C8.3,0,8,0,7.7,0.1H6.8C4.6,1,3,3,1.8,5.3C0.7,7.6,0,10.2,0,12.5L0,12.5z" />
                  </svg>

                </span>
              </button>
              {showCall &&
                <div className="dropdown-menu chat-drop-area dropdown-menu-end" id="message-dropdown-menu">
                  <div className="message-content-body-main-left" style={{ width: "100%" }}>
                    <div className="message-content-body-header">
                      <p style={{ fontWeight: 600, fontSize: "larger" }}>Calls</p>
                      <div className="message-content-body-header-icons">
                        <div className="dropstart">
                          <button className="dropdown-toggle chat-settings-btn" type="button" data-bs-toggle="dropdown" data-bs-auto-close="outside" aria-expanded="false">
                            {/* <i className="fa-solid fa-ellipsis" aria-hidden="true"></i> */}
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="feather feather-more-horizontal"
                            >
                              <circle cx="12" cy="12" r="1" />
                              <circle cx="19" cy="12" r="1" />
                              <circle cx="5" cy="12" r="1" />
                            </svg>
                          </button>
                          <div className="dropdown-menu drop-nav-chat-inner-setting" style={{ width: "300px" }}>
                            <div className="chat-settings-main">
                              <div className="chat-settings-header">
                                <p style={{ fontWeight: 600 }}>Calls Settings</p>
                                <p style={{ fontSize: "12px", opacity: 0.7 }}>Customize your messenger experience</p>
                              </div>
                              <hr />
                              <div className="chat-settings-main-toggle">
                                <div className="chat-settings-toggle-left">
                                  <i className="fa-solid fa-phone" aria-hidden="true"></i>
                                  <p>Incoming Call Sounds</p>
                                </div>
                                <label className="switch">
                                  <input type="checkbox" id="number-pool-checkbox" />
                                  <span className="slider round" style={{ backgroundColor: "#ccc", color: "#22B573" }}></span>
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                        <i className="fa-regular fa-pen-to-square" id="header-dialer" aria-hidden="true"></i>
                      </div>
                    </div>
                    <div className="message-content-body-inbox-search-box align-items-center" style={{ visibility: "hidden", height: 0 }}>
                      <span>
                        <i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
                      </span>
                      <input type="text" placeholder="Search" />
                    </div>
                    <div className="chatbox-contacts-list chatbox-contacts-list-nav header-chatbox-contacts-list" id="voice_inbox_chat">
                      <div id="voiceloader">
                      </div>
                      <div id="voice-contactlists">
                      </div>
                    </div>
                  </div>
                </div>}
            </div>
          </div>
        </div>


        {toggleButton}
        {children}

        <MobileMenu position={menuPosition} />

        {/* {showNotifButton && <NotificationDialogTrigger />} */}
      </div>

    </div >
  );
}

interface DesktopMenuProps {
  position: NavbarProps['menuPosition'];
}
function DesktopMenu({ position }: DesktopMenuProps) {
  return (
    <CustomMenu
      className="mx-14 text-sm max-md:hidden"
      itemClassName={isActive =>
        clsx(
          'opacity-90 hover:underline hover:opacity-100',
          isActive && 'opacity-100',
        )
      }
      menu={position}
    />
  );
}

interface MobileMenuProps {
  position: NavbarProps['menuPosition'];
}
function MobileMenu({ position }: MobileMenuProps) {
  const navigate = useNavigate();
  const menu = useCustomMenu(position);

  if (!menu?.items.length) {
    return null;
  }

  const handleItemClick = (item: MenuItemConfig) => {
    if (isAbsoluteUrl(item.action)) {
      window.open(item.action, item.target)?.focus();
    } else {
      navigate(item.action);
    }
  };

  return (
    <MenuTrigger>
      <IconButton className="md:hidden" aria-label="Toggle menu">
        <MenuIcon />
      </IconButton>
      <Menu>
        {menu.items.map(item => {
          const Icon = item.icon && createSvgIconFromTree(item.icon);
          return (
            <Item
              value={item.action}
              onSelected={() => handleItemClick(item)}
              key={item.id}
              startIcon={Icon && <Icon />}
            >
              <Trans message={item.label} />
            </Item>
          );
        })}
      </Menu>
    </MenuTrigger>
  );
}

function MobileSidebar() {
  const [isOpen, setIsOpen] = useState(false);
  const sidebarRef = useRef<HTMLDivElement | null>(null);

  // Close sidebar when clicking outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Prevent body scroll when sidebar is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const sidebarItems = [
    { label: 'Dashboard', icon: 'https://aicpanel.smsit.ai/assets/img/left-nav-icons/dashboard.png', href: 'https://aicpanel.smsit.ai/home' },
    { label: 'Staff AI', icon: 'https://aicpanel.smsit.ai/assets/img/left-nav-icons/staff-ai.png', href: 'https://aicpanel.smsit.ai/staff-ai', divider: true },
    { label: 'Communication', icon: 'https://aicpanel.smsit.ai/assets/img/left-nav-icons/icon2.png', href: 'https://aicpanel.smsit.ai/users/conversation-dashboard' },
    { label: 'Sales', icon: 'https://aicpanel.smsit.ai/assets/img/left-nav-icons/icon3.png', href: 'https://aicpanel.smsit.ai/sales/dashboard' },
    { label: 'Marketing', icon: 'https://aicpanel.smsit.ai/assets/img/left-nav-icons/icon4.png', href: 'https://aicpanel.smsit.ai/smart-tool' },
    { label: 'Management', icon: 'https://aicpanel.smsit.ai/assets/img/left-nav-icons/icon5.png', href: 'https://aicpanel.smsit.ai/users/management-dashboard' },
    { label: 'Artificial Intelligence', icon: 'https://aicpanel.smsit.ai/assets/img/left-nav-icons/icon6.png', href: 'https://aicpanel.smsit.ai/users/artificial-intelligence-dashboard' },
    { label: 'Automation', icon: 'https://aicpanel.smsit.ai/assets/img/left-nav-icons/icon7.png', href: 'https://aicpanel.smsit.ai/users/automation-dashboard' },
    { label: 'Reports', icon: 'https://aicpanel.smsit.ai/assets/img/left-nav-icons/icon8.png', href: 'https://aicpanel.smsit.ai/logs' },
    { label: 'Reseller Accounts', icon: 'https://aicpanel.smsit.ai/assets/img/left-nav-icons/icon9.png', href: 'https://aicpanel.smsit.ai/agency/accounts' },
    { label: 'My Organization', icon: 'https://aicpanel.smsit.ai/assets/img/left-nav-icons/icon10.png', href: 'https://aicpanel.smsit.ai/users/account' },
  ];

  return (
    <>
      {/* Mobile Logo + Hamburger Bar - only visible on mobile (max-width: 1024px) */}
      <div className="buildit-mobile-sidebar-header">
        <a href="https://aicpanel.smsit.ai/home" className="buildit-mobile-logo">
          <img
            src="https://aicpanel.smsit.ai/assets/img/SMSit-logo.png"
            alt="SMS-iT Logo"
            style={{ maxHeight: '30px', width: 'auto' }}
          />
        </a>
        <button
          className={`buildit-hamburger-btn ${isOpen ? 'open' : ''}`}
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle sidebar menu"
          type="button"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>

      {/* Overlay */}
      {isOpen && (
        <div
          className="buildit-sidebar-overlay"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar Panel */}
      <div
        ref={sidebarRef}
        className={`buildit-mobile-sidebar-panel ${isOpen ? 'open' : ''}`}
      >
        <div className="buildit-sidebar-panel-header">
          <a href="https://aicpanel.smsit.ai/home">
            <img
              src="https://aicpanel.smsit.ai/assets/img/SMSit-logo.png"
              alt="SMS-iT Logo"
              style={{ maxHeight: '30px', width: 'auto' }}
            />
          </a>
          <button
            className="buildit-sidebar-close-btn"
            onClick={() => setIsOpen(false)}
            aria-label="Close sidebar"
            type="button"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <nav className="buildit-sidebar-nav">
          {sidebarItems.map((item, index) => (
            <div key={index}>
              <a
                href={item.href}
                className="buildit-sidebar-nav-item"
                onClick={() => setIsOpen(false)}
              >
                <img src={item.icon} alt="" style={{ width: '24px', height: '24px', marginRight: '12px' }} />
                <span>{item.label}</span>
              </a>
              {item.divider && <hr style={{ margin: '4px 16px', borderColor: 'rgba(255,255,255,0.1)' }} />}
            </div>
          ))}
        </nav>
      </div>
    </>
  );
}

function getColorStyle(color: string, textColor?: string): string {
  switch (color) {
    case 'primary':
      return `bg-primary ${textColor || 'text-on-primary'} border-b-primary`;
    case 'bg':
      return `bg ${textColor || 'text-main'} border-b`;
    case 'bg-alt':
      return `bg-alt ${textColor || 'text-main'} border-b`;
    case 'transparent':
      return `bg-transparent ${textColor || 'text-white'}`;
    default:
      return `${color} ${textColor}`;
  }
}
